import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useInView } from "@/hooks/useInView";
import {
  InstagramBrandTile,
  WhatsAppBrandTile,
} from "@/components/landing/shared/ChannelBrandIcons";
import { cn } from "@/lib/utils";

const channelTileBase =
  "flex shrink-0 items-center justify-center rounded-xl shadow-[0_4px_16px_-2px_hsl(240_10%_15%_/_0.12)] ring-1 ring-white/50";

type EnterDelay = "none" | "short" | "medium" | "long";

const enterDelayClass: Record<EnterDelay, string> = {
  none: "",
  short: "motion-safe:delay-100",
  medium: "motion-safe:delay-200",
  long: "motion-safe:delay-300",
};

function enterMotion(isInView: boolean, delay: EnterDelay = "none") {
  return cn(
    "motion-safe:transition-[opacity,transform] motion-safe:duration-700 motion-safe:ease-[cubic-bezier(0.22,1,0.36,1)]",
    enterDelayClass[delay],
    !isInView &&
      "translate-y-3 opacity-0 motion-reduce:translate-y-0 motion-reduce:opacity-100",
    isInView && "translate-y-0 opacity-100",
  );
}

function LogoTile({ isInView, className }: { isInView: boolean; className?: string }) {
  return (
    <div className={cn(enterMotion(isInView, "short"), className)}>
      <div
        className={cn(
          channelTileBase,
          "h-12 w-12 bg-card sm:h-14 sm:w-14",
          isInView && "motion-safe:animate-cta-float motion-safe:[animation-delay:0.75s]",
        )}
      >
        <img
          src="/icon.png?v=11"
          alt="FabChat"
          width={44}
          height={44}
          className="h-9 w-9 sm:h-10 sm:w-10 object-contain"
        />
      </div>
    </div>
  );
}

function ChannelTiles({
  isInView,
  className,
}: {
  isInView: boolean;
  className?: string;
}) {
  return (
    <div className={cn("flex shrink-0 flex-row items-center gap-2 sm:gap-2.5", className)}>
      <div className={enterMotion(isInView, "medium")}>
        <WhatsAppBrandTile
          className={cn(
            channelTileBase,
            "h-12 w-12 sm:h-14 sm:w-14",
            isInView && "motion-safe:animate-cta-float-delay-1 motion-safe:[animation-delay:0.85s]",
          )}
          iconClassName="h-6 w-6 sm:h-7 sm:w-7"
        />
      </div>
      <div className={enterMotion(isInView, "long")}>
        <InstagramBrandTile
          className={cn(
            channelTileBase,
            "h-12 w-12 sm:h-14 sm:w-14",
            isInView && "motion-safe:animate-cta-float-delay-2 motion-safe:[animation-delay:0.95s]",
          )}
          iconClassName="h-6 w-6 sm:h-7 sm:w-7"
        />
      </div>
    </div>
  );
}

export const FaqCtaBanner = () => {
  const { ref, isInView } = useInView({ threshold: 0.15 });

  return (
    <section
      ref={ref}
      className="bg-muted/20 pb-16 pt-10 md:pb-20 md:pt-12"
      aria-labelledby="faq-cta-heading"
    >
      <div className="container mx-auto max-w-6xl px-4">
        <div
          className={cn(
            "relative overflow-hidden rounded-2xl border border-border/75 bg-card px-5 py-5 shadow-[0_2px_12px_-2px_hsl(240_10%_15%_/_0.12)] sm:px-8 sm:py-6",
            "ring-1 ring-primary/10",
            enterMotion(isInView),
            isInView &&
              "motion-safe:shadow-[0_4px_20px_-4px_hsl(262_75%_55%_/_0.12)] motion-safe:transition-shadow motion-safe:duration-700",
          )}
        >
          <div className="relative flex flex-col items-stretch gap-5 md:flex-row md:items-center md:gap-8">
            <LogoTile isInView={isInView} className="hidden md:block" />

            <div className="flex min-w-0 flex-1 flex-col gap-4">
              <div className={enterMotion(isInView, "short")}>
                <h2
                  id="faq-cta-heading"
                  className="text-pretty text-lg font-bold leading-snug tracking-tight text-foreground sm:text-xl md:text-2xl"
                >
                  Pronto para atender em escala sem perder o tom humano?
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-foreground/75 sm:text-base">
                  Ative agentes de IA no WhatsApp e no Instagram em poucos minutos, sem código, com CRM
                  integrado para sua equipe acompanhar tudo. Escolha o plano ideal na sequência.
                </p>
              </div>

              <div
                className={cn(
                  "flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-5",
                  enterMotion(isInView, "medium"),
                )}
              >
                <Button
                  asChild
                  className="h-11 w-full rounded-full bg-primary px-8 text-sm font-semibold text-primary-foreground shadow-md shadow-primary/25 hover:bg-primary/90 motion-safe:transition-transform motion-safe:duration-200 motion-safe:hover:scale-[1.02] sm:w-auto md:h-12 md:px-10"
                >
                  <Link to="/auth?tab=signup">Começar agora</Link>
                </Button>

                <ChannelTiles isInView={isInView} className="justify-center md:hidden" />
              </div>
            </div>

            <ChannelTiles isInView={isInView} className="hidden md:flex" />
          </div>
        </div>
      </div>
    </section>
  );
};
